import React, { useState } from 'react';
import BlogPostsList from './BlogPostsList';
import BlogPostEditor from './BlogPostEditor';
import BlogCategoriesPanel from './BlogCategoriesPanel';
import BlogSettingsPanel from './BlogSettingsPanel';

/**
 * AdminBlog — root view for the Blog admin tab.
 *
 * Sub-views (controlled by `view` state):
 *   - 'posts'      : list of all posts (+ "New post" button)
 *   - 'editor'     : create/edit a single post (granular controls + live preview)
 *   - 'categories' : Greetings / Souk / Food / Culture CRUD
 *   - 'settings'   : global blog settings (airline name, hero cities, footer text)
 *
 * Lives in its own folder under components/admin/blog/ — no shared code with the
 * rest of the admin panel beyond the props (API base, admin token, notification fn).
 */
export default function AdminBlog({ API, adminToken, isDarkMode, showNotification }) {
  const [view, setView] = useState('posts');
  const [editingSlug, setEditingSlug] = useState(null); // null = new post; string = edit

  const openEditor = (slug) => {
    setEditingSlug(slug);
    setView('editor');
  };

  const closeEditor = () => {
    setEditingSlug(null);
    setView('posts');
  };

  const tabs = [
    { id: 'posts',      label: 'Posts',      icon: '✈' },
    { id: 'categories', label: 'Categories', icon: '🏷' },
    { id: 'settings',   label: 'Settings',   icon: '⚙' },
  ];

  const bg = isDarkMode ? 'bg-gray-900' : 'bg-gray-50';
  const panelBg = isDarkMode ? 'bg-gray-800' : 'bg-white';
  const text = isDarkMode ? 'text-white' : 'text-gray-900';
  const border = isDarkMode ? 'border-gray-700' : 'border-gray-200';
  const muted = isDarkMode ? 'text-gray-400' : 'text-gray-500';

  return (
    <div className={`min-h-[80vh] ${bg} ${text} rounded-xl p-6`}>

      {/* Header */}
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">✈ Royal DarijaPro Air — Blog</h1>
          <p className={`text-sm mt-1 ${muted}`}>
            Manage posts, categories, and global blog settings. Public pages render server-side at
            <code className="mx-1 px-1.5 py-0.5 rounded bg-gray-700/30 text-xs">/blog</code>
            (own Mongo DB, no overlap with the rest of DarijaPro).
          </p>
        </div>
        <a
          href="/blog"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-bold hover:from-blue-700 hover:to-purple-700"
        >
          ↗ Open live blog
        </a>
      </div>

      {/* Sub-tabs (only shown when not in the editor) */}
      {view !== 'editor' && (
        <div className={`flex gap-1 mb-6 border-b ${border}`}>
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setView(t.id)}
              className={`px-5 py-3 text-sm font-bold transition-colors border-b-2 -mb-px ${
                view === t.id
                  ? 'border-blue-500 text-blue-500'
                  : `border-transparent ${muted} hover:text-blue-500`
              }`}
            >
              <span className="mr-1.5">{t.icon}</span>{t.label}
            </button>
          ))}
        </div>
      )}

      {/* View content */}
      <div className={`${panelBg} rounded-xl p-6 ${border} border`}>
        {view === 'posts' && (
          <BlogPostsList
            API={API}
            adminToken={adminToken}
            isDarkMode={isDarkMode}
            showNotification={showNotification}
            onEdit={openEditor}
            onNew={() => openEditor(null)}
          />
        )}
        {view === 'editor' && (
          <BlogPostEditor
            API={API}
            adminToken={adminToken}
            isDarkMode={isDarkMode}
            showNotification={showNotification}
            slug={editingSlug}
            onClose={closeEditor}
          />
        )}
        {view === 'categories' && (
          <BlogCategoriesPanel
            API={API}
            adminToken={adminToken}
            isDarkMode={isDarkMode}
            showNotification={showNotification}
          />
        )}
        {view === 'settings' && (
          <BlogSettingsPanel
            API={API}
            adminToken={adminToken}
            isDarkMode={isDarkMode}
            showNotification={showNotification}
          />
        )}
      </div>
    </div>
  );
}
